import './scss/style.scss'

import React, { Suspense, useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { hasAuthParams, useAuth } from 'react-oidc-context'
import { useDispatch } from 'react-redux'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { ErrorResponse } from 'oidc-client-ts'
import PropTypes from 'prop-types'

import { setToken } from 'src/features/auth/authSlice'

import { CSpinner } from '@coreui/react-pro'

const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))
const Initializing = React.lazy(() => import('./views/initializing/Initializing'))
const NotAuthenticated = React.lazy(() => import('./views/error/NotAuthenticated'))
const NotRegistered = React.lazy(() => import('./views/error/NotRegistered'))

function App({ userManager }) {
  const dispatch = useDispatch()
  const auth = useAuth()
  const [hasTriedSignin, setHasTriedSignin] = useState(false)

  useEffect(() => {
    let isCancelled = false

    if (isCancelled) {
      return
    }

    dispatch(setToken(auth?.user?.access_token))

    return () => {
      isCancelled = true
    }
  }, [dispatch, auth.user])

  // oidc-client-ts no reintenta la renovación si falla por red (wifi, tablet saliendo de reposo):
  // reintentar hasta que funcione y solo volver al login si Keycloak rechaza la sesión.
  useEffect(() => {
    let reintento
    const renovar = () => userManager.signinSilent().catch(alFallar)
    const alFallar = (err) => {
      if (err instanceof ErrorResponse) {
        userManager.signinRedirect()
        return
      }
      clearTimeout(reintento)
      reintento = setTimeout(renovar, 30000)
    }
    const siCaducado = async () => {
      if (document.visibilityState === 'visible' && (await userManager.getUser())?.expired)
        renovar()
    }

    userManager.events.addSilentRenewError(alFallar)
    window.addEventListener('online', siCaducado)
    document.addEventListener('visibilitychange', siCaducado)
    return () => {
      clearTimeout(reintento)
      userManager.events.removeSilentRenewError(alFallar)
      window.removeEventListener('online', siCaducado)
      document.removeEventListener('visibilitychange', siCaducado)
    }
  }, [userManager])

  useEffect(() => {
    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !hasTriedSignin
    ) {
      auth.signinRedirect()
      setHasTriedSignin(true)
    }
  }, [auth, hasTriedSignin])

  if (auth.isLoading) {
    return <Initializing />
  }

  if (!auth.isAuthenticated) {
    return <NotAuthenticated />
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<CSpinner color="primary" />}>
        <Routes>
          <Route
            path="*"
            element={(() => {
              return <DefaultLayout />
            })()}
          />
          <Route path="/not-registered" name="Not Registered" element={<NotRegistered />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

App.propTypes = {
  userManager: PropTypes.shape({
    events: PropTypes.shape({
      addSilentRenewError: PropTypes.func.isRequired,
      removeSilentRenewError: PropTypes.func.isRequired,
    }).isRequired,
    signinSilent: PropTypes.func.isRequired,
    signinRedirect: PropTypes.func.isRequired,
    getUser: PropTypes.func.isRequired,
  }).isRequired,
}

const hoc = withTranslation()(App)
export default hoc
